import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import AppleYaVk from '../../image/apple_yavk.jpg';
import AndroidYaVk from '../../image/android_yavk.jpg';
import { DialogOpenImageItemDiv } from './DialogOpenImageItemDiv';

interface Props {
  fixedPlatform?: 'ios' | 'android';
  device?: 'current' | 'second';
}

export const SetupsItemUstroistvDiv = ({
  fixedPlatform,
  device = 'second',
}: Props) => {
  const [open, setOpen] = useState(false);
  const [imagePokaz, setImagePokaz] = useState<string>();
  const [platform, setPlatform] = useState('ios');
  const isIos = (fixedPlatform ?? platform) === 'ios';
  const selectedTabId = isIos ? 'restore-setup-ios' : 'restore-setup-android';
  const image = isIos ? AppleYaVk : AndroidYaVk;
  const imageLabel = `Увеличить схему настройки ${isIos ? 'iOS' : 'Android'}${
    device === 'current' ? ' на устройстве с подпиской' : ''
  }`;
  const steps = isIos
    ? [
        {
          id: 'app-store',
          text: (
            <>
              Откройте <strong>App Store</strong>.
            </>
          ),
        },
        { id: 'profile', text: 'Нажмите на своё имя в правом верхнем углу.' },
        {
          id: 'account',
          text: 'В открывшемся окне снова нажмите на своё имя.',
        },
        {
          id: 'region',
          text: (
            <>
              В разделе <strong>«Страна/регион»</strong> выберите{' '}
              <strong>Россию</strong>.
            </>
          ),
        },
      ]
    : [
        {
          id: 'settings',
          text: (
            <>
              Откройте <strong>настройки телефона</strong>.
            </>
          ),
        },
        {
          id: 'date-time',
          text: (
            <>
              Найдите раздел <strong>«Дата и время»</strong>.
            </>
          ),
        },
        {
          id: 'time-zone',
          text: (
            <>
              В настройках часового пояса выберите{' '}
              <strong>регион или город России</strong>.
            </>
          ),
        },
      ];

  return (
    <Stack width="100%" spacing={2}>
      <Typography
        component="p"
        fontFamily="Comfortaa"
        fontSize="14px"
        lineHeight={1.75}
      >
        Включите вход через Яндекс ID или ВК ID{' '}
        {device === 'current'
          ? 'на устройстве с подпиской'
          : 'на втором устройстве'}
        .
        {!fixedPlatform && ' Выполните настройку для его операционной системы.'}
      </Typography>
      {!fixedPlatform && (
        <Tabs
          aria-label="Операционная система второго устройства"
          value={platform}
          onChange={(_, val) => setPlatform(val)}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              fontFamily: 'Comfortaa',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'none',
              minHeight: 44,
              color: '#66616e',
            },
            '& .MuiTab-root.Mui-selected': { color: '#8b249f' },
            '& .MuiTabs-indicator': { backgroundColor: '#8b249f' },
          }}
        >
          <Tab
            id="restore-setup-ios"
            aria-controls="restore-setup-panel"
            value="ios"
            label="iOS"
          />
          <Tab
            id="restore-setup-android"
            aria-controls="restore-setup-panel"
            value="android"
            label="Android"
          />
        </Tabs>
      )}
      <Stack
        role={!fixedPlatform ? 'tabpanel' : undefined}
        id={!fixedPlatform ? 'restore-setup-panel' : undefined}
        aria-labelledby={!fixedPlatform ? selectedTabId : undefined}
        spacing={2}
      >
        <Box
          component="ol"
          sx={{
            m: 0,
            pl: 3,
            display: 'grid',
            gap: 1.5,
            '& li::marker': { fontWeight: 700, color: '#66616e' },
          }}
        >
          {steps.map(({ id, text }) => (
            <Typography
              component="li"
              key={id}
              fontFamily="Comfortaa"
              fontSize="14px"
              lineHeight={1.75}
              sx={{ pl: 0.5 }}
            >
              {text}
            </Typography>
          ))}
        </Box>
        <Tooltip title={imageLabel}>
          <ButtonBase
            aria-label={imageLabel}
            onClick={() => {
              setImagePokaz(image);
              setOpen(true);
            }}
            sx={{
              display: 'block',
              width: '100%',
              overflow: 'hidden',
              border: '1px solid #dcd9e2',
              borderRadius: '8px',
              bgcolor: '#fff',
              '&.Mui-focusVisible': {
                outline: '3px solid #9c27b0',
                outlineOffset: '3px',
              },
            }}
          >
            <Box
              component="img"
              src={image}
              alt={imageLabel}
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                aspectRatio: isIos ? '1280 / 738' : '1280 / 988',
              }}
            />
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 32,
                height: 32,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e2e9',
                borderRadius: '6px',
                color: '#56505f',
              }}
            >
              <ZoomInIcon fontSize="small" />
            </Box>
          </ButtonBase>
        </Tooltip>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ p: 1.5, bgcolor: '#eaf2f5', borderLeft: '3px solid #4f8298' }}
      >
        <RestartAltIcon
          sx={{ fontSize: 20, color: '#3c6b7e', flexShrink: 0, mt: 0.25 }}
        />
        <Typography
          component="p"
          fontFamily="Comfortaa"
          fontSize="13px"
          lineHeight={1.7}
        >
          После смены настроек <strong>перезапустите приложение Учи Мед</strong>
          .
        </Typography>
      </Stack>
      <DialogOpenImageItemDiv
        open={open}
        imagePokaz={imagePokaz}
        onClose={() => setOpen(false)}
      />
    </Stack>
  );
};

SetupsItemUstroistvDiv.defaultProps = {
  fixedPlatform: undefined,
  device: 'second',
};
